/**
 * Multi-segment initialization for loaded NIfTI masks (#685).
 *
 * A saved mask can carry several stones, one per segment index (1, 2, 3, …).
 * Cornerstone's `addContourSegmentationAnnotation` reads
 * `segmentation.segments[segmentIndex].locked` and throws a TypeError when that
 * entry is `undefined`. Only segment index 1 gets lazily created during normal
 * setup, so for the 2nd+ stone the entry is missing — the add throws, the
 * annotation never registers with the contour rep (invisible mask, dead
 * lasso/bump, per-slice exception flood on load).
 *
 * `segmentation.segmentIndex.setActiveSegmentIndex(segId, idx)` is the API that
 * materializes `segments[idx]` (`{ segmentIndex, label, locked:false, … }` — see
 * @cornerstonejs/tools stateManagement/segmentation/segmentIndex.js). Calling it
 * for every index a mask uses, before the add-loop, prevents the throw.
 */

import { logger } from "@/utils/logger";

/** Cornerstone-tools `segmentation` namespace, narrowed to what we touch. */
interface SegmentationApi {
  segmentIndex?: {
    setActiveSegmentIndex?: (segmentationId: string, segmentIndex: number) => void;
  };
}

/**
 * Collect every unique non-zero segment index a mask writes across its
 * slices-with-data, sorted ascending. Background (0) is excluded.
 */
export function collectMaskSegmentIndices(
  readVoxel: (r: number, c: number, s: number) => number,
  slicesWithData: number[],
  nRows: number,
  nCols: number,
): number[] {
  const indices = new Set<number>();
  for (const s of slicesWithData) {
    for (let r = 0; r < nRows; r++) {
      for (let c = 0; c < nCols; c++) {
        const v = readVoxel(r, c, s);
        if (v > 0) indices.add(v);
      }
    }
  }
  return [...indices].sort((a, b) => a - b);
}

/**
 * Ensure a `segments[idx]` entry exists on the contour segmentation for every
 * index the mask uses, so the subsequent `addContourSegmentationAnnotation`
 * `.locked` read can't TypeError. Idempotent; safe if the API is unavailable.
 */
export function initContourSegmentIndices(
  segmentation: SegmentationApi,
  contourSegmentationId: string,
  segmentIndices: number[],
): void {
  for (const idx of segmentIndices) {
    try {
      segmentation.segmentIndex?.setActiveSegmentIndex?.(contourSegmentationId, idx);
    } catch (err) {
      logger.warn(
        `[niftiMaskSegmentInit] setActiveSegmentIndex failed for ${contourSegmentationId}#${idx}:`,
        err,
      );
    }
  }
}
